import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CorporateEntityService } from '../app/api/services/CorporateEntityService';
import { withAuth, getAuthToken, logout } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { formatPhoneNumber } from '../lib/utils';
import '../lib/apiConfig'; // Initialize API config

function Directory() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCompanies, setFilteredCompanies] = useState([]);

    // Load all companies on mount
    useEffect(() => {
        const loadCompanies = async () => {
            try {
                // Check authentication first - try to get session
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.error('Error getting session:', sessionError);
                }

                if (!session) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('No session found - user needs to login');
                    }
                    setError('You must be logged in to view the directory.');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
                }

                // Double-check we have a token
                const token = await getAuthToken();
                if (!token) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('No auth token available after session check');
                    }
                    setError('You must be logged in to view the directory.');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
                }

                // Fetch all corporate entities (no managerid filter to get all companies)
                const response = await withAuth(async () => {
                    return await CorporateEntityService.getApiV2CorporateEntity(
                        undefined, // search
                        undefined, // industry
                        undefined, // size
                        undefined, // managerid - no filter to get all companies
                        true, // activeflag - only show active companies
                        'ONE', // publishStatus - only show published companies
                        undefined, // moq
                        undefined, // productOrService
                        undefined, // certificate
                        undefined, // country
                        undefined, // page
                        undefined, // pageSize
                        undefined, // limit
                        undefined, // offset
                        'legalname', // sort by legal name
                        'asc' // order
                    );
                });

                if (response.data) {
                    setCompanies(response.data);
                    setFilteredCompanies(response.data);
                }
            } catch (error) {
                console.error('Error loading companies:', error);
                setError('Failed to load directory. Please try again.');
                if (error?.status === 401) {
                    await logout();
                    setTimeout(() => navigate('/login'), 2000);
                }
            } finally {
                setLoading(false);
            }
        };

        loadCompanies();
    }, [navigate]);

    // Filter companies based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredCompanies(companies);
            return;
        }

        const searchLower = searchTerm.toLowerCase();
        const filtered = companies.filter((company) => {
            const name = company.legalname?.toLowerCase() || '';
            const website = company.website?.toLowerCase() || '';
            const industry = company.industry?.toLowerCase() || '';
            const description = company.description?.toLowerCase() || '';
            const size = company.size?.toLowerCase() || '';

            return (
                name.includes(searchLower) ||
                website.includes(searchLower) ||
                industry.includes(searchLower) ||
                description.includes(searchLower) ||
                size.includes(searchLower)
            );
        });

        setFilteredCompanies(filtered);
    }, [searchTerm, companies]);

    if (loading) {
        return (
            <main className="App-main">
                <div className="flex justify-center items-center min-h-[60vh] flex-col gap-4">
                    <div className="text-xl text-muted-foreground">Loading directory...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="App-main">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-foreground mb-2">Company Directory</h1>
                    <p className="text-muted-foreground">Browse all companies in the database</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        {error}
                    </div>
                )}

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search companies by name, industry, website, or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-3 pl-10 border border-input rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none bg-background"
                        />
                        <svg
                            className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </div>
                    {searchTerm && (
                        <p className="mt-2 text-sm text-muted-foreground">
                            Showing {filteredCompanies.length} of {companies.length} companies
                        </p>
                    )}
                </div>

                {/* Companies Grid */}
                {filteredCompanies.length === 0 ? (
                    <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                        <p className="text-muted-foreground">
                            {searchTerm
                                ? 'No companies found matching your search.'
                                : 'No companies found in the directory.'}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCompanies.map((company) => (
                            <div
                                key={company.id}
                                onClick={() => navigate(`/company/${company.id}`, { state: { company } })}
                                className="bg-card rounded-lg shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
                            >
                                {/* Company Header */}
                                <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                                    <h3 className="text-xl font-semibold text-foreground mb-3">
                                        {company.legalname || 'Unnamed Company'}
                                    </h3>
                                    {company.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                            {company.description}
                                        </p>
                                    )}
                                </div>

                                {/* Company Details */}
                                <div className="px-6 py-5 space-y-3">
                                    {company.website && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">🌐</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Website
                                                </div>
                                                <a
                                                    href={company.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-brand-orange hover:underline break-all text-sm"
                                                >
                                                    {company.website}
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {company.phone && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">📞</span>
                                            <div className="flex-1">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Phone
                                                </div>
                                                <div className="text-foreground text-sm">{formatPhoneNumber(company.phone)}</div>
                                            </div>
                                        </div>
                                    )}

                                    {company.industry && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">🏭</span>
                                            <div className="flex-1">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Industry
                                                </div>
                                                <div className="text-foreground text-sm">{company.industry}</div>
                                            </div>
                                        </div>
                                    )}

                                    {company.size && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">🏢</span>
                                            <div className="flex-1">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Company Size
                                                </div>
                                                <div className="text-foreground text-sm">{company.size}</div>
                                            </div>
                                        </div>
                                    )}

                                    {company.MOQ && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">📦</span>
                                            <div className="flex-1">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Minimum Order Quantity
                                                </div>
                                                <div className="text-foreground text-sm">{company.MOQ}</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Locations */}
                                    {company.location && company.location.length > 0 && (
                                        <div className="flex items-start">
                                            <span className="text-muted-foreground mr-2 text-sm">📍</span>
                                            <div className="flex-1">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                    Locations ({company.location.length})
                                                </div>
                                                <div className="space-y-1">
                                                    {company.location.slice(0, 2).map((location) => (
                                                        <div key={location.id} className="text-sm text-foreground">
                                                            {location.city && location.country_code
                                                                ? `${location.city}, ${location.country_code}`
                                                                : location.address || 'Location'}
                                                        </div>
                                                    ))}
                                                    {company.location.length > 2 && (
                                                        <div className="text-xs text-muted-foreground">
                                                            +{company.location.length - 2} more
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Certifications */}
                                    {company.location &&
                                        company.location.some(
                                            (loc) => loc.locationcertifications && loc.locationcertifications.length > 0
                                        ) && (
                                            <div className="flex items-start">
                                                <span className="text-muted-foreground mr-2 text-sm">🏆</span>
                                                <div className="flex-1">
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        Certifications
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {company.location
                                                            .flatMap((loc) => loc.locationcertifications || [])
                                                            .slice(0, 3)
                                                            .map((cert, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="inline-block px-2 py-1 text-xs bg-muted text-foreground rounded"
                                                                >
                                                                    {cert.certification?.certname || 'Certification'}
                                                                </span>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}

export default Directory;

